import { useState, useEffect, useRef, useCallback } from 'react';
import dashboardService from '../api';

const useDashboardData = (useCaseId) => {
  const [widgetContents, setWidgetContents] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showTabPopup, setShowTabPopup] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [editingTabId, setEditingTabId] = useState(null);
  const [transcriptionText, setTranscriptionText] = useState("");
  const [processedContext, setProcessedContext] = useState("");
  const [widgetContexts, setWidgetContexts] = useState({});
  const [useCases, setUseCases] = useState([]);
  const [tools, setTools] = useState([]);
  const [summaryPrompt, setSummaryPrompt] = useState(
    "Provide a comprehensive summary based on the widget data and context provided. Focus on key insights, trends, and actionable information."
  );
  const [isEditingSummaryPrompt, setIsEditingSummaryPrompt] = useState(false);
  const [tempSummaryPrompt, setTempSummaryPrompt] = useState("");

  // Refs
  const persistentWidgetContentsRef = useRef({});
  const initialFetchDone = useRef(false);
  const prevTranscriptionRef = useRef("");
  const renderCountRef = useRef(0);

  // Widget form state
  const [newWidget, setNewWidget] = useState({
    title: '',
    widgetType: 'text',
    tool: '',
    toolId: '',
    endpoint: '',
    prompt: '',
    visualPrompt: '',
    tabId: '',
    hasAvatar: false
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [loading]);

  // Set user ID immediately
  useEffect(() => {
    const storedUserId = sessionStorage.getItem('user_id') || 'U1757535255';
    setUserId(storedUserId);
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {

      setLoading(true);
      try {
        let clientsData = [];
        try {
          clientsData = await dashboardService.fetchClients();
          // setClients(clientsData);

          if (clientsData.length > 0) {
            setSelectedClient(clientsData[0]);
          }
        } catch (err) {
          // console.error("Error fetching clients:", err);
          // Continue with empty clients
        }

        // 2. Fetch use cases and tools in parallel
        try {
          const [useCasesData, toolsData] = await Promise.all([
            dashboardService.fetchUseCases(),
            dashboardService.fetchTools()
          ]);

          setUseCases(useCasesData);
          setTools(toolsData);

          if (toolsData.length > 0) {
            const defaultTool = toolsData[0];
            setNewWidget(prev => ({
              ...prev,
              tool: defaultTool.name,
              toolId: defaultTool.id,
              endpoint: defaultTool.api_endpoint || ''
            }));
          }
        } catch (err) {
          console.error("Error fetching use cases or tools:", err);
          // Continue with empty data
        }

        if (useCaseId) {
          try {
            const userIdToUse = sessionStorage.getItem('user_id') || 'U1757535255';

            // Use the new fetchWidgetsAndTabs method
            const widgetsAndTabsData = await dashboardService.fetchWidgetsAndTabs(useCaseId, userIdToUse);

            if (widgetsAndTabsData.isProcessing) {
              console.log("Tab creation in progress, will fetch data again shortly");
              setTimeout(() => {
                setLoading(false);
                fetchAllData();
              }, 1000);
              return;
            }

            // Process tabs
            if (widgetsAndTabsData.tabs && Array.isArray(widgetsAndTabsData.tabs)) {
              setTabs(widgetsAndTabsData.tabs);

              // Set active tab to the first one if available
              if (widgetsAndTabsData.tabs.length > 0) {
                setActiveTab(widgetsAndTabsData.tabs[0].id || widgetsAndTabsData.tabs[0].tab_id);

                // Set the default tab for new widgets
                setNewWidget(prev => ({
                  ...prev,
                  tabId: widgetsAndTabsData.tabs[0].id || widgetsAndTabsData.tabs[0].tab_id
                }));
              }
            }

            const allWidgets = [];

            widgetsAndTabsData.tabs.forEach(tab => {
              if (tab.widgets && Array.isArray(tab.widgets)) {
                const tabWidgets = tab.widgets.map(widget => ({
                  id: widget.widget_id,
                  title: widget.widget_name,
                  subtitle: widget.subtitle || "",
                  prompt: widget.prompt || "",
                  chartPrompt: widget.visual_prompt && widget.visual_prompt.trim() !== "" ? widget.visual_prompt : null,
                  customerId: clientsData.length > 0 ? clientsData[0].id : "CUST007",
                  tool: widget.tool_name || "SQL Query Assistant",
                  endpoint: widget.endpoint || ":5009/sql-ask",
                  content: "",
                  collection: widget.collection_name || "default_collection",
                  tabId: tab.tab_id, // Associate widget with its tab
                  key: `${widget.widget_id}_${clientsData.length > 0 ? clientsData[0].id : "CUST007"}`
                }));

                allWidgets.push(...tabWidgets);
              }
            });

            setWidgets(allWidgets);

          } catch (err) {
            console.error("Error fetching widgets and tabs:", err);
            // Continue with empty widgets and tabs
          }
        }

        // 4. Load summary prompt
        if (useCaseId) {
          try {
            const summaryData = await dashboardService.loadSummaryPrompt(useCaseId);
            if (summaryData && summaryData.prompt) {
              setSummaryPrompt(summaryData.prompt);
            }
          } catch (err) {
            console.error("Error loading summary prompt:", err);
            // Continue with default prompt
          }
        }

      } catch (err) {
        console.error("Error in data fetching:", err);
        setError("Failed to load dashboard data");
      } finally {
        
        setLoading(false);
        initialFetchDone.current = true;
      }
    };

    // Start fetching data
    fetchAllData();
  }, [useCaseId]); // Only depend on useCaseId

  const getWidgetContentByTitle = useCallback((title) => {
    const widget = widgets.find(w => w.title === title);
    if (widget && widget.id && widgetContents[widget.id]) {
      return widgetContents[widget.id];
    }
    return null;
  }, [widgets, widgetContents]);

  const updateWidgetContexts = useCallback((newProcessedContext, fullTranscription) => {
    if (!widgets.length) return;

    const summaryContent = getWidgetContentByTitle("Summary Generator");
    let hasChanges = false;
    const newContexts = { ...widgetContexts };

    widgets.forEach(widget => {
      if (widget.tool === 'Transcript Insight Engine' || widget.tool === 'Transcript Analysis') {
        let contextToUse = widget.tool === 'Transcript Insight Engine' && summaryContent
          ? summaryContent
          : newProcessedContext;

        if (newContexts[widget.id] !== contextToUse) {
          newContexts[widget.id] = contextToUse;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setWidgetContexts(newContexts);
    }
  }, [widgets, widgetContexts, getWidgetContentByTitle]);

  useEffect(() => {
    prevTranscriptionRef.current = transcriptionText;

    const lines = transcriptionText.split('\n');
    const filteredLines = lines.filter(line => line.trim().startsWith('##'));
    const cleanedLines = filteredLines.map(line => line.replace(/^##\s*/, ''));
    const newProcessedContext = cleanedLines.join('\n');

    if (newProcessedContext !== processedContext) {
      setProcessedContext(newProcessedContext);
      const timeoutId = setTimeout(() => {
        updateWidgetContexts(newProcessedContext, transcriptionText);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [transcriptionText, processedContext, updateWidgetContexts]);

  useEffect(() => {
    if (widgets.length === 0 || Object.keys(persistentWidgetContentsRef.current).length === 0) return;

    const restoredContents = {};
    let hasRestoredContent = false;

    widgets.forEach(widget => {
      const savedContent = persistentWidgetContentsRef.current[widget.id];
      if (savedContent) {
        restoredContents[widget.id] = savedContent;
        hasRestoredContent = true;
      }
    });

    if (hasRestoredContent) {
      setWidgetContents(prev => ({ ...prev, ...restoredContents }));
    }
  }, [widgets]);

  useEffect(() => {
    // Initialize contexts for transcript widgets when they're loaded
    if (widgets.length > 0 && transcriptionText) {
      const transcriptWidgets = widgets.filter(w =>
        w.tool === 'Transcript Analysis' || w.tool === 'Transcript Insight Engine');

      if (transcriptWidgets.length > 0) {
        setWidgetContexts(prev => {
          const newContexts = { ...prev };
          let hasChanges = false;

          transcriptWidgets.forEach(widget => {
            if (!newContexts[widget.id] && transcriptionText) {
              newContexts[widget.id] = transcriptionText;
              hasChanges = true;
            }
          });

          return hasChanges ? newContexts : prev;
        });
      }
    }
  }, [widgets, transcriptionText]);

  const handleClientChange = useCallback((client) => {
    if (client.id === selectedClient?.id) return;

    setSelectedClient(client);
    setWidgetContents({});

    const updatedWidgets = widgets.map(widget => ({
      ...widget,
      key: `${widget.id}_${client.id}`,
      customerId: client.id
    }));

    setWidgets(updatedWidgets);
  }, [selectedClient, widgets]);

  const handleWidgetContentChange = useCallback((widgetId, content) => {
    if (!content || content.trim() === '') return;

    persistentWidgetContentsRef.current[widgetId] = content;

    setWidgetContents(prev => {
      const updatedContents = { ...prev, [widgetId]: content };
      return updatedContents;
    });

    const widget = widgets.find(w => w.id === widgetId);
    if (widget && widget.title === "Summary Generator") {
      setTimeout(() => {
        updateWidgetContexts(processedContext, transcriptionText);
      }, 100);
    }
  }, [widgets, processedContext, transcriptionText, updateWidgetContexts]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === 'tool') {
      const selectedTool = tools.find(tool => tool.name === value);
      if (selectedTool) {
        setNewWidget(prev => ({
          ...prev,
          tool: selectedTool.name,
          toolId: selectedTool.id,
          endpoint: selectedTool.api_endpoint || ''
        }));
      }
    } else {
      setNewWidget(prev => ({ ...prev, [name]: value }));
    }
  }, [tools]);

  const refetchWidgets = useCallback(async () => {
    if (!useCaseId) return;

    try {
      setLoading(true);

      const userIdToUse = sessionStorage.getItem('user_id') || 'U1757535255';

      // Fetch widgets and tabs
      const widgetsAndTabsData = await dashboardService.fetchWidgetsAndTabs(useCaseId, userIdToUse);

      // Process tabs
      if (widgetsAndTabsData.tabs && Array.isArray(widgetsAndTabsData.tabs)) {
        setTabs(widgetsAndTabsData.tabs);
      }

      const allWidgets = [];

      widgetsAndTabsData.tabs.forEach(tab => {
        if (tab.widgets && Array.isArray(tab.widgets)) {
          const tabWidgets = tab.widgets.map(widget => ({
            id: widget.widget_id,
            title: widget.widget_name,
            subtitle: widget.subtitle || "",
            prompt: widget.prompt || "",
            chartPrompt: widget.visual_prompt && widget.visual_prompt.trim() !== "" ? widget.visual_prompt : null,
            customerId: clients.length > 0 ? clients[0].id : "CUST007",
            tool: widget.tool_name || "SQL Query Assistant",
            endpoint: widget.endpoint || ":5009/sql-ask",
            content: "",
            collection: widget.collection_name || "default_collection",
            tabId: tab.tab_id, // Associate widget with its tab
            key: `${widget.widget_id}_${clients.length > 0 ? clients[0].id : "CUST007"}`
          }));

          allWidgets.push(...tabWidgets);
        }
      });

      setWidgets(allWidgets);

    } catch (err) {
      console.error("Error refetching widgets:", err);
      // Don't show an alert here to avoid disrupting the user experience
    } finally {
      setLoading(false);
    }
  }, [useCaseId, clients]);

  const handleSaveWidget = useCallback(async (widgetData = null) => {
    try {
      // Use the passed widget data or fall back to the state
      const dataToSave = widgetData || newWidget;

      // Make sure we're using the active tab
      const tabIdToUse = activeTab;

      const widgetId = Date.now().toString().slice(-5);
      const newWidgetObj = {
        id: widgetId,
        title: dataToSave.title,
        subtitle: "",
        prompt: dataToSave.prompt,
        chartPrompt: dataToSave.widgetType === 'visualization' ? dataToSave.visualPrompt : null,
        customerId: selectedClient?.id,
        tool: dataToSave.tool,
        endpoint: dataToSave.endpoint || ":5009/sql-ask",
        content: "",
        tabId: tabIdToUse
      };

      // Temporarily add the widget to the UI for immediate feedback
      setWidgets(prev => [...prev, newWidgetObj]);

      const body = {
        id: widgetId,
        name: dataToSave.title,
        tool_prompt: dataToSave.prompt,
        visual_prompt: dataToSave.widgetType === 'visualization' ? dataToSave.visualPrompt : null,
        user_id: userId,
        tool_id: dataToSave.toolId,
        use_case_id: useCaseId || 'tst1',
        tab_id: tabIdToUse
      };

      await dashboardService.createWidget(body);

      if ((dataToSave.tool === 'Transcript Insight Engine' || dataToSave.tool === 'Transcript Analysis') &&
        (processedContext || transcriptionText)) {
        setWidgetContexts(prev => {
          const contextToUse = dataToSave.tool === 'Transcript Insight Engine' && getWidgetContentByTitle("Summary Generator")
            ? getWidgetContentByTitle("Summary Generator")
            : processedContext;
          return { ...prev, [widgetId]: contextToUse };
        });
      }

      // Reset the form
      setNewWidget({
        title: '',
        widgetType: 'text',
        tool: tools.length > 0 ? tools[0].name : '',
        toolId: tools.length > 0 ? tools[0].id : '',
        endpoint: tools.length > 0 ? (tools[0].api_endpoint || '') : '',
        prompt: '',
        visualPrompt: '',
        tabId: tabIdToUse,
        hasAvatar: false
      });
      setShowPopup(false);

      // Refetch widgets to ensure we have the latest data
      await refetchWidgets();

      return true; // Return success status
    } catch (err) {
      console.error("Error saving new widget:", err);
      alert("Failed to save widget. Please try again.");
      return false; // Return failure status
    }
  }, [newWidget, selectedClient, userId, useCaseId, tools, processedContext, transcriptionText, getWidgetContentByTitle, activeTab, refetchWidgets]);

  const handleDeleteWidget = useCallback(async (id) => {
    try {
      // Optimistically remove the widget from UI
      setWidgets(widgets.filter(w => w.id !== id));
      setWidgetContexts(prev => {
        const newContexts = { ...prev };
        delete newContexts[id];
        return newContexts;
      });

      // Delete from backend
      await dashboardService.deleteWidget(id);

      // Refetch to ensure we have the latest data
      await refetchWidgets();

    } catch (err) {
      console.error("Error deleting widget:", err);
      alert("Widget was removed from view but may not have been deleted from the database.");

      // Still refetch to ensure UI is in sync with backend
      await refetchWidgets();
    }
  }, [widgets, refetchWidgets]);


  const handleEditSummaryPrompt = useCallback(() => {
    setTempSummaryPrompt(summaryPrompt);
    setIsEditingSummaryPrompt(true);
  }, [summaryPrompt]);

  const handleSaveSummaryPrompt = useCallback(async () => {
    try {
      const response = await dashboardService.saveSummaryPrompt(useCaseId, tempSummaryPrompt);

      if (response.success) {
        setSummaryPrompt(tempSummaryPrompt);
        setIsEditingSummaryPrompt(false);
        alert('Summary prompt saved successfully!');
      } else {
        console.error('Failed to save summary prompt:', response);
        alert('Failed to save summary prompt. Please try again.');
      }
    } catch (error) {
      console.error('Error saving summary prompt:', error);
      alert('Error saving summary prompt: ' + (error.message || 'Unknown error'));
    }
  }, [useCaseId, tempSummaryPrompt]);

  const handleCancelEditSummaryPrompt = useCallback(() => {
    setTempSummaryPrompt("");
    setIsEditingSummaryPrompt(false);
  }, []);

  const handleTranscriptionComplete = useCallback((text, isPartialUpdate = false) => {
    if (!text || (text === prevTranscriptionRef.current && !isPartialUpdate)) {
      return;
    }

    const fullText = isPartialUpdate && prevTranscriptionRef.current
      ? prevTranscriptionRef.current + "\n" + text
      : text;

    prevTranscriptionRef.current = fullText;

    setTranscriptionText(fullText);

    const textToProcess = isPartialUpdate ? text : fullText;
    const lines = textToProcess.split('\n');
    const filteredLines = lines.filter(line => line.trim().startsWith('##'));
    const cleanedLines = filteredLines.map(line => line.replace(/^##\s*/, ''));
    let newProcessedContext;
    if (isPartialUpdate && processedContext) {
      newProcessedContext = processedContext + "\n" + cleanedLines.join('\n');
    } else {
      newProcessedContext = cleanedLines.join('\n');
    }

    setProcessedContext(newProcessedContext);

    const widgetsToUpdate = widgets.filter(widget =>
      widget.tool === 'Transcript Analysis' || widget.tool === 'Transcript Insight Engine'
    );

    if (widgetsToUpdate.length > 0) {
      const newContexts = {};

      Object.keys(widgetContexts).forEach(key => {
        newContexts[key] = widgetContexts[key];
      });

      widgetsToUpdate.forEach(widget => {
        if (widget.tool === 'Transcript Analysis') {
          newContexts[widget.id] = fullText;
        }
        else if (widget.tool === 'Transcript Insight Engine') {
          const summaryContent = getWidgetContentByTitle("Summary Generator");
          const contextToUse = summaryContent || newProcessedContext;
          newContexts[widget.id] = contextToUse;
        }
      });

      setTimeout(() => {
        setWidgetContexts(newContexts);
      }, 0);
    }
  }, [widgets, widgetContexts, processedContext, getWidgetContentByTitle]);

  const handleCreateTab = useCallback(async () => {
    if (!newTabName.trim()) {
      alert("Please enter a tab name");
      return;
    }

    try {
      // Generate a unique tab ID
      const tabId = `tab_${Date.now().toString().slice(-5)}`;

      // Create new tab object with consistent property naming
      const newTab = {
        tab_id: tabId,
        tab_name: newTabName,
        use_case_id: useCaseId,
        widgets: [] // Initialize with empty widgets array
      };

      // Update UI immediately for responsive feedback
      setTabs(prev => [...prev, newTab]);

      // If this is the first tab, set it as active
      if (tabs.length === 0) {
        setActiveTab(tabId);
        setNewWidget(prev => ({
          ...prev,
          tabId: tabId
        }));
      }

      // Reset form state
      setNewTabName('');
      setShowTabPopup(false);

      // Send request to backend
      await dashboardService.createTab(tabId, newTabName, useCaseId);

      // No need to refetch if we've already updated the UI correctly
    } catch (err) {
      console.error("Error creating tab:", err);
      alert("Failed to create tab. Please try again.");

      // Revert UI change on error by refetching
      await refetchWidgets();
    }
  }, [newTabName, useCaseId, tabs, refetchWidgets]);

  const handleDeleteTab = useCallback(async (tabId) => {
    // Using window.confirm instead of confirm to avoid ESLint error
    if (!window.confirm("Are you sure you want to delete this tab? All widgets in this tab will also be deleted.")) {
      return;
    }

    try {
      // Update UI immediately for responsive feedback
      const updatedTabs = tabs.filter(tab => tab.tab_id !== tabId);
      setTabs(updatedTabs);

      // Remove widgets associated with this tab
      const updatedWidgets = widgets.filter(widget => widget.tabId !== tabId);
      setWidgets(updatedWidgets);

      // If the active tab was deleted, set a new active tab
      if (activeTab === tabId) {
        if (updatedTabs.length > 0) {
          setActiveTab(updatedTabs[0].tab_id);

          // Update the default tab for new widgets
          setNewWidget(prev => ({
            ...prev,
            tabId: updatedTabs[0].tab_id
          }));
        } else {
          setActiveTab(null);
        }
      }

      // Send request to backend
      await dashboardService.deleteTab(tabId);

    } catch (err) {
      console.error("Error deleting tab:", err);
      alert("Failed to delete tab. Please try again.");

      // Revert UI change on error by refetching
      await refetchWidgets();
    }
  }, [tabs, activeTab, widgets, refetchWidgets]);

  const handleEditTabName = useCallback((tabId, currentName) => {
    setEditingTabId(tabId);
    setNewTabName(currentName);
    setShowTabPopup(true);
  }, []);

  const handleSaveTabName = useCallback(async () => {
    if (!newTabName.trim()) {
      alert("Please enter a tab name");
      return;
    }

    try {
      // Update UI immediately
      setTabs(prev => prev.map(tab =>
        tab.tab_id === editingTabId ? { ...tab, tab_name: newTabName } : tab
      ));

      // Reset form state
      setNewTabName('');
      setEditingTabId(null);
      setShowTabPopup(false);

      // Send request to backend
      await dashboardService.updateTabName(editingTabId, newTabName);

    } catch (err) {
      console.error("Error updating tab name:", err);
      alert("Failed to update tab name. Please try again.");

      // Revert UI change on error by refetching
      await refetchWidgets();
    }
  }, [editingTabId, newTabName, refetchWidgets]);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);

    // Update the default tab for new widgets
    setNewWidget(prev => ({
      ...prev,
      tabId: tabId
    }));
  }, []);


  const hasTranscriptionWidget = widgets.some(
    widget => widget.tool === "Transcript Insight Engine" || widget.tool === "Transcript Analysis"
  );

  const getCurrentUseCaseName = useCallback(() => {
    const currentUseCase = useCases.find(uc => uc.id === (useCaseId || 'tst1'));
    return currentUseCase ? currentUseCase.name : 'Wealth Management';
  }, [useCases, useCaseId]);

  // Get widgets for the active tab
  const activeTabWidgets = useCallback(() => {
    return widgets.filter(widget => widget.tabId === activeTab);
  }, [widgets, activeTab]);

  return {
    widgetContents,
    // clients,
    // selectedClient,
    widgets,
    tabs,
    activeTab,
    activeTabWidgets: activeTabWidgets(),
    loading,
    error,
    userId,
    showPopup,
    showTabPopup,
    newTabName,
    editingTabId,
    transcriptionText,
    processedContext,
    widgetContexts,
    useCases,
    tools,
    newWidget,
    summaryPrompt,
    isEditingSummaryPrompt,
    tempSummaryPrompt,
    hasTranscriptionWidget,
    refetchWidgets,

    setShowPopup,
    setShowTabPopup,
    setWidgets,
    setNewTabName,
    setTabs,
    setActiveTab,
    setError,
    setLoading,
    setEditingTabId,
    handleClientChange,
    handleWidgetContentChange,
    handleInputChange,
    handleSaveWidget,
    handleDeleteWidget,
    handleTranscriptionComplete,
    handleEditSummaryPrompt,
    handleSaveSummaryPrompt,
    handleCancelEditSummaryPrompt,
    setTempSummaryPrompt,
    setIsEditingSummaryPrompt,
    getCurrentUseCaseName,

    // Tab management
    handleCreateTab,
    handleDeleteTab,
    handleEditTabName,
    handleSaveTabName,
    handleTabChange
  };
};

export default useDashboardData;